/**
 * DEC-001: Relation Widget Component
 * 
 * Displays related documents (e.g., Payment Entries for Sales Order) in
 * configurable modes: list, badge, or summary.
 */

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2 } from 'lucide-react';
import type { RelationWidgetConfig } from '@/lib/views/types';
import { erp } from '@/lib/erp';
import type { FrappeFilterArray } from '@/lib/erp/filterMapper';
import { buildDocRoute } from '@/lib/config/docRoutes';

interface RelationWidgetProps {
  widget: RelationWidgetConfig;
  sourceDoc: Record<string, any>;
  // disabled prop removed - can be added back when needed for action disabling
}

/**
 * Hook to fetch related documents based on widget configuration
 */
function useRelationDocs(
  widget: RelationWidgetConfig,
  sourceDoc: Record<string, any>
) {
  const sourceValue = sourceDoc[widget.sourceField];
  
  return useQuery({
    queryKey: ['relation', widget.id, widget.targetDoctype, sourceValue],
    enabled: !!sourceValue, // Only query if source value exists
    queryFn: async () => {
      const filters: FrappeFilterArray = [];
      
      // Build filter for source field
      // Use 4-tuple format: [doctype, field, operator, value]
      // This matches FrappeFilterArray format and ensures proper filtering
      if (widget.query.filterField && sourceValue !== undefined) {
        filters.push([widget.targetDoctype, widget.query.filterField, '=', sourceValue]);
      }
      
      // Build filter for doctype field (if specified)
      if (widget.query.filterDoctypeField && sourceDoc.doctype) {
        filters.push([widget.targetDoctype, widget.query.filterDoctypeField, '=', sourceDoc.doctype]);
      }
      
      // Guard: If no filters, don't query (misconfig)
      if (filters.length === 0) {
        if (import.meta.env?.DEV) {
          console.warn('[RelationWidget] No filters configured', {
            widgetId: widget.id,
            targetDoctype: widget.targetDoctype,
            sourceField: widget.sourceField,
            sourceValue,
          });
        }
        return [] as Record<string, any>[];
      }
      
      // Use existing list API (single params object)
      const result = await erp.docs.docTypeListWithPagination<Record<string, any>>({
        doctype: widget.targetDoctype,
        filters,
        pageSize: 100, // Reasonable default for related docs
      });
      
      return (result.data || []) as Record<string, any>[];
    },
  });
}

/**
 * Handle action based on behaviorKey
 * 
 * NOTE: Uses targetDoctype (not doc.doctype) because API list responses
 * don't guarantee doctype is included in the row data.
 */
function useActionHandler(targetDoctype: string) {
  const navigate = useNavigate();
  
  return (behaviorKey: string, doc: Record<string, any>) => {
    switch (behaviorKey) {
      case 'view': {
        // Use targetDoctype from widget config, not from doc (which may be undefined)
        const route = buildDocRoute(targetDoctype, doc.name);
        navigate(route);
        break;
      }
      
      // Future: Other behaviors via flow engine
      default:
        if (import.meta.env?.DEV) {
          console.warn('[RelationWidget] Unknown action behaviorKey', {
            behaviorKey,
            docName: doc.name,
            targetDoctype,
          });
        }
        break;
    }
  };
}

/**
 * Render list mode - full table
 */
function RelationWidgetList({
  widget,
  docs,
  onAction,
}: {
  widget: RelationWidgetConfig;
  docs: Record<string, any>[];
  onAction: (behaviorKey: string, doc: Record<string, any>) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              {widget.displayFields.map(field => (
                <TableHead key={field}>{field}</TableHead>
              ))}
              {widget.actions && widget.actions.length > 0 && (
                <TableHead>Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.map(doc => (
              <TableRow key={doc.name}>
                {widget.displayFields.map(field => (
                  <TableCell key={field}>
                    {doc[field] !== undefined && doc[field] !== null
                      ? String(doc[field])
                      : '-'}
                  </TableCell>
                ))}
                {widget.actions && widget.actions.length > 0 && (
                  <TableCell>
                    <div className="flex gap-2">
                      {widget.actions.map(action => (
                        <Button
                          key={action.id}
                          size="sm"
                          variant="outline"
                          onClick={() => onAction(action.behaviorKey, doc)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * Render badge mode - compact badges
 */
function RelationWidgetBadge({
  widget,
  docs,
  onAction,
}: {
  widget: RelationWidgetConfig;
  docs: Record<string, any>[];
  onAction: (behaviorKey: string, doc: Record<string, any>) => void;
}) {
  const firstField = widget.displayFields[0] || 'name';
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {docs.map(doc => (
            <Badge
              key={doc.name}
              variant="outline"
              className="cursor-pointer hover:bg-muted"
              onClick={() => onAction('view', doc)}
            >
              {doc[firstField] || doc.name}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Render summary mode - aggregated count
 */
function RelationWidgetSummary({
  widget,
  docs,
}: {
  widget: RelationWidgetConfig;
  docs: Record<string, any>[];
}) {
  const count = docs.length;
  
  // Compute metrics if configured
  const metrics = widget.summaryMetrics?.map((metric: { type: 'count' | 'sum'; field?: string; label?: string }) => {
    if (metric.type === 'sum' && metric.field) {
      const sum = docs.reduce((acc, doc) => {
        const value = doc[metric.field!];
        const num = typeof value === 'number' ? value : parseFloat(value);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
      return {
        label: metric.label || `Total ${metric.field}`,
        value: sum,
      };
    }
    return null;
  }).filter(Boolean) as { label: string; value: number }[];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            Total: {count}
          </div>
          {metrics.map((metric, idx) => (
            <div key={idx} className="text-sm">
              <span className="text-muted-foreground">{metric.label}:</span>{' '}
              <span className="font-medium">{metric.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Relation Widget Component
 */
export function RelationWidget({ widget, sourceDoc }: RelationWidgetProps) {
  const sourceValue = sourceDoc[widget.sourceField];
  const { data: docs, isLoading, error } = useRelationDocs(widget, sourceDoc);
  const handleAction = useActionHandler(widget.targetDoctype);
  
  // Don't render if no source value
  if (!sourceValue) {
    return null;
  }
  
  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">{widget.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-destructive">
            Failed to load {widget.title}
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Empty state
  if (!docs || docs.length === 0) {
    return null; // Quietly hide if no related records
  }
  
  // Render based on mode
  switch (widget.mode) {
    case 'list':
      return (
        <RelationWidgetList
          widget={widget}
          docs={docs}
          onAction={handleAction}
        />
      );
    
    case 'badge':
      return (
        <RelationWidgetBadge
          widget={widget}
          docs={docs}
          onAction={handleAction}
        />
      );
    
    case 'summary':
      return (
        <RelationWidgetSummary
          widget={widget}
          docs={docs}
        />
      );
    
    default:
      // Unknown mode - fallback to summary
      if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
        console.warn('[RelationWidget] Unknown mode', {
          widgetId: widget.id,
          mode: widget.mode,
        });
      }
      return (
        <RelationWidgetSummary
          widget={widget}
          docs={docs}
        />
      );
  }
}

